import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { BACKEND_URL } from '../config';
import { useModalClose } from '../hooks/useModalClose';

const OutboundInvoices = ({ theme, onCreateInvoice }) => {
  const { t } = useLanguage();
  const [allOrders, setAllOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('withInvoice'); // 'all', 'closed', 'withInvoice', 'withoutInvoice'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [createNewOrder, setCreateNewOrder] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterType, allOrders]);

  // Close create invoice modal on ESC/back
  useModalClose(() => {
    setShowCreateModal(false);
    setSelectedOrderId('');
    setCreateNewOrder(false);
  }, showCreateModal);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/orders`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();

      // Remove duplicates by order ID
      const uniqueOrders = Array.from(
        new Map(data.map(order => [order.id, order])).values()
      );

      // Sort by date (newest first)
      uniqueOrders.sort((a, b) => new Date(b.closed_at || b.modified) - new Date(a.closed_at || a.modified));

      setAllOrders(uniqueOrders);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allOrders];

    // Apply type filter
    if (filterType === 'closed') {
      filtered = filtered.filter(order => order.closed === 1);
    } else if (filterType === 'withInvoice') {
      filtered = filtered.filter(order => order.invoice_number);
    } else if (filterType === 'withoutInvoice') {
      filtered = filtered.filter(order => order.closed === 1 && !order.invoice_number);
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(order =>
        order.id.toString().includes(term) ||
        (order.name && order.name.toLowerCase().includes(term)) ||
        (order.table_name && order.table_name.toLowerCase().includes(term)) ||
        (order.invoice_number && order.invoice_number.toLowerCase().includes(term))
      );
    }

    setFilteredOrders(filtered);
  };

  const handleCreateNewOrder = async () => {
    try {
      // First, get the list of tables to use the first available one
      const tablesResponse = await fetch(`${BACKEND_URL}/api/tables`);
      if (!tablesResponse.ok) {
        throw new Error('Failed to fetch tables');
      }
      const tables = await tablesResponse.json();

      if (tables.length === 0) {
        throw new Error('No tables available. Please create a table first.');
      }

      // Use the first table for the invoice order
      const firstTable = tables[0];

      // Create a new standalone order for the invoice
      const response = await fetch(`${BACKEND_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          OrderName: `Invoice ${new Date().toLocaleString()}`,
          tableId: firstTable.id,
          user_id: 1 // Default user
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const newOrderId = data.id;

      // Close the order immediately since it's just for invoice
      await fetch(`${BACKEND_URL}/api/orders/${newOrderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: data.name,
          table_id: data.table_id,
          closed: 1
        }),
      });

      // Close modal and open invoice generator with new order
      setShowCreateModal(false);
      setSelectedOrderId('');
      setCreateNewOrder(false);
      onCreateInvoice(newOrderId);

      // Refresh orders list
      fetchOrders();
    } catch (err) {
      console.error('Error creating new order:', err);
      alert(`${t('error')}: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: theme?.colors?.text || '#000'
      }}>
        {t('loading')}
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        gap: '1rem',
        flexWrap: 'wrap'
      }}>
        <h3 style={{ margin: 0, color: theme?.colors?.text || '#000' }}>
          {t('outboundInvoices')}
        </h3>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              backgroundColor: theme?.colors?.primary || '#1976d2',
              color: theme?.colors?.textLight || '#fff',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 'bold'
            }}
          >
            ➕ {t('createInvoice')}
          </button>
          <button
            onClick={fetchOrders}
            style={{
              backgroundColor: theme?.colors?.secondary || '#666',
              color: theme?.colors?.textLight || '#fff',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            🔄 {t('refresh')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div>
          <label style={{
            display: 'block',
            marginBottom: '0.25rem',
            fontSize: '0.9rem',
            color: theme?.colors?.text || '#000'
          }}>
            {t('search')}:
          </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={`${t('orders')} ID, ${t('name')}, ${t('invoiceNumber')}...`}
            style={{
              width: '100%',
              padding: '0.5rem',
              borderRadius: '4px',
              border: `1px solid ${theme?.colors?.border || '#ccc'}`,
              backgroundColor: theme?.colors?.surface || '#fff',
              color: theme?.colors?.text || '#000'
            }}
          />
        </div>

        <div>
          <label style={{
            display: 'block',
            marginBottom: '0.25rem',
            fontSize: '0.9rem',
            color: theme?.colors?.text || '#000'
          }}>
            {t('filter')}:
          </label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              borderRadius: '4px',
              border: `1px solid ${theme?.colors?.border || '#ccc'}`,
              backgroundColor: theme?.colors?.surface || '#fff',
              color: theme?.colors?.text || '#000'
            }}
          >
            <option value="all">{t('all')} {t('orders')}</option>
            <option value="closed">{t('closed')} {t('orders')}</option>
            <option value="withInvoice">{t('with')} {t('invoice')}</option>
            <option value="withoutInvoice">{t('without')} {t('invoice')}</option>
          </select>
        </div>
      </div>

      {/* Results Count */}
      <div style={{
        marginBottom: '1rem',
        color: theme?.colors?.textSecondary || '#666',
        fontSize: '0.9rem'
      }}>
        {t('showing')} {filteredOrders.length} {t('of')} {allOrders.length} {t('orders')}
      </div>

      {/* Orders List */}
      <div style={{
        display: 'grid',
        gap: '1rem'
      }}>
        {filteredOrders.length === 0 ? (
          <div style={{
            padding: '2rem',
            textAlign: 'center',
            color: theme?.colors?.textSecondary || '#666'
          }}>
            {t('noOrdersFound')}
          </div>
        ) : (
          filteredOrders.map(order => (
            <div
              key={order.id}
              style={{
                backgroundColor: theme?.colors?.surface || '#fff',
                border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                borderRadius: '8px',
                padding: '1rem',
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: '1rem',
                alignItems: 'center'
              }}
            >
              <div>
                <div style={{
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  color: theme?.colors?.text || '#000',
                  marginBottom: '0.5rem'
                }}>
                  {t('orders')} #{order.id} - {order.name || 'N/A'}
                </div>
                <div style={{
                  fontSize: '0.9rem',
                  color: theme?.colors?.textSecondary || '#666',
                  marginBottom: '0.25rem'
                }}>
                  <strong>{t('Table')}:</strong> {order.table_name || 'N/A'} |
                  <strong> {t('status')}:</strong> {order.closed === 1 ? t('closed') : t('open')} |
                  <strong> {t('total')}:</strong> ${(parseFloat(order.order_revenue) || 0).toFixed(2)}
                </div>
                <div style={{
                  fontSize: '0.9rem',
                  color: theme?.colors?.textSecondary || '#666'
                }}>
                  <strong>{t('date')}:</strong> {new Date(order.closed_at || order.modified).toLocaleDateString()} {new Date(order.closed_at || order.modified).toLocaleTimeString()}
                </div>
                {order.invoice_number && (
                  <div style={{
                    marginTop: '0.5rem',
                    padding: '0.25rem 0.5rem',
                    backgroundColor: theme?.colors?.success || '#4caf50',
                    color: '#fff',
                    borderRadius: '4px',
                    display: 'inline-block',
                    fontSize: '0.85rem',
                    fontWeight: 'bold'
                  }}>
                    ✓ {t('invoice')}: {order.invoice_number}
                  </div>
                )}
              </div>
              <div>
                <button
                  onClick={() => onCreateInvoice(order.id)}
                  style={{
                    backgroundColor: theme?.colors?.primary || '#1976d2',
                    color: theme?.colors?.textLight || '#fff',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap'
                  }}
                >
                  📄 {order.invoice_number ? t('viewInvoice') : t('createInvoice')}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div onClick={() => {
          setShowCreateModal(false);
          setSelectedOrderId('');
          setCreateNewOrder(false);
        }} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: theme?.colors?.surface || '#fff',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{
              marginTop: 0,
              marginBottom: '1.5rem',
              color: theme?.colors?.text || '#000'
            }}>
              {t('createInvoice')}
            </h3>

            {/* Option Selection */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{
                display: 'flex',
                gap: '1.5rem',
                marginBottom: '1rem'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  color: theme?.colors?.text || '#000'
                }}>
                  <input
                    type="radio"
                    name="orderOption"
                    checked={!createNewOrder}
                    onChange={() => {
                      setCreateNewOrder(false);
                      setSelectedOrderId('');
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                  {t('useExistingOrder')}
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  color: theme?.colors?.text || '#000'
                }}>
                  <input
                    type="radio"
                    name="orderOption"
                    checked={createNewOrder}
                    onChange={() => {
                      setCreateNewOrder(true);
                      setSelectedOrderId('');
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                  {t('createNewOrder')}
                </label>
              </div>
            </div>

            {/* Existing Order Selection */}
            {!createNewOrder && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem',
                  color: theme?.colors?.text || '#000'
                }}>
                  {t('selectOrder')}:
                </label>
                <select
                  value={selectedOrderId}
                  onChange={(e) => setSelectedOrderId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                    backgroundColor: theme?.colors?.surface || '#fff',
                    color: theme?.colors?.text || '#000',
                    fontSize: '1rem'
                  }}
                >
                  <option value="">{t('selectOrder')}...</option>
                  {allOrders.map(order => (
                    <option key={order.id} value={order.id}>
                      #{order.id} - {order.name || 'N/A'} - {order.table_name || 'N/A'} - ${(parseFloat(order.order_revenue) || 0).toFixed(2)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* New Order Info */}
            {createNewOrder && (
              <div style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: theme?.colors?.background || '#f5f5f5',
                borderRadius: '4px',
                border: `1px solid ${theme?.colors?.border || '#ccc'}`
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '0.9rem',
                  color: theme?.colors?.textSecondary || '#666'
                }}>
                  {t('newOrderWillBeCreated')}
                </p>
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setSelectedOrderId('');
                  setCreateNewOrder(false);
                }}
                style={{
                  backgroundColor: theme?.colors?.secondary || '#666',
                  color: theme?.colors?.textLight || '#fff',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => {
                  if (createNewOrder) {
                    handleCreateNewOrder();
                  } else if (selectedOrderId) {
                    onCreateInvoice(parseInt(selectedOrderId));
                    setShowCreateModal(false);
                    setSelectedOrderId('');
                    setCreateNewOrder(false);
                  }
                }}
                disabled={!createNewOrder && !selectedOrderId}
                style={{
                  backgroundColor: (createNewOrder || selectedOrderId) ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.border || '#ccc'),
                  color: theme?.colors?.textLight || '#fff',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: (createNewOrder || selectedOrderId) ? 'pointer' : 'not-allowed',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
              >
                {createNewOrder ? t('createNewOrder') : t('createInvoice')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutboundInvoices;
